// client/customer/src/pages/Reserve/by-service/SelectService.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext, type SelectedService } from '../../../contexts/useReserveContext';
import {
  fetchStaffOptionsForDate,
} from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import {
  fetchServicesForDateSelection,
  type ServiceForDateSelection,
} from '../../../utils/reservation/by-Date/fetchServicesForDateSelection';
import {
  fetchServicesForServiceReserve,
  type ServiceForServiceReserve,
} from '../../../utils/reservation/by-service/fetchServicesForServiceReserve';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';
import { postAvailableTimesByService } from '../../../utils/reservation/by-service/postAvailableTimesByService';

// ✅ 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import ServiceCategoryAccordion, {
  type ServiceAccordionCategory,
} from '../ServiceCategoryAccordion';
import { useI18n } from '../../../i18n';

const toNumberSafe = (v: unknown): number => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);
const ANY_STAFF_LABEL = 'Anyone is okay';

const SelectServiceByService: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    skippedStoreSelectionByService,
    selectedDate,
    selectedStaffId,
    selectedStaffName,
    selectedStartTime,
    isFreeReservation,
    selectedServices,
    setSelectedServices,
    setSelectedDate,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedStartTime,
    setIsFreeReservation,
  } = useReserveContext();

  const [rows, setRows] = useState<Array<ServiceForServiceReserve | ServiceForDateSelection>>([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  // guard
  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-service/store');
      return;
    }
    if (isEditing && (!selectedDate || (!selectedStaffId && !isFreeReservation) || !selectedStartTime)) {
      toast.info(t('service.returnAfterDateStaffTime'));
      navigate('/reserve/by-service/confirm-reservation');
    }
  }, [storeId, selectedDate, selectedStaffId, selectedStartTime, isFreeReservation, isEditing, navigate, t]);

  // load
  useEffect(() => {
    const load = async () => {
      if (!storeId) return;
      setLoading(true);
      try {
        const data = isEditing
          ? await fetchServicesForDateSelection({
              storeId,
              date: selectedDate!,
              staffId: isFreeReservation ? undefined : selectedStaffId!,
              anyone: isFreeReservation,
            })
          : await fetchServicesForServiceReserve(storeId);
        setRows(data);
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || t('service.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedDate, selectedStaffId, isFreeReservation, isEditing, t]);

  const selectedServiceIds = useMemo(
    () => selectedServices.map((s) => s.serviceId),
    [selectedServices]
  );

  const categoryGroups: ServiceAccordionCategory<ArrayElement<typeof rows>>[] = useMemo(() => {
    const map = new Map<number | null, ServiceAccordionCategory<ArrayElement<typeof rows>>>();

    rows.forEach((sv) => {
      const key = (sv.category_id ?? null) as number | null;
      if (!map.has(key)) {
        map.set(key, {
          category_id: key,
          category_name: sv.category_name || t('menu.other'),
          color_class: sv.color_class ?? null,
          services: [],
        });
      }
      map.get(key)!.services.push(sv);
    });

    return Array.from(map.values());
  }, [rows, t]);

  const totalAmount = useMemo(
    () => selectedServices.reduce((sum, s) => sum + toNumberSafe(s.price), 0),
    [selectedServices]
  );

  // durationは表示しないが、合計はNextの可否や後続計算に必要なので保持
  const totalMinutes = useMemo(
    () => selectedServices.reduce((sum, s) => sum + toNumberSafe(s.duration_minutes), 0),
    [selectedServices]
  );

  const toggleService = (sv: ArrayElement<typeof rows>) => {
    setValidationMessage(null);
    const exists = selectedServiceIds.includes(sv.service_id);

    if (exists) {
      setSelectedServices(selectedServices.filter((s) => s.serviceId !== sv.service_id));
    } else {
      const item: SelectedService = {
        serviceId: sv.service_id,
        serviceName: sv.service_name,
        categoryName: sv.category_name || t('menu.other'),
        price: toNumberSafe(sv.price),
        duration_minutes: toNumberSafe(sv.duration_minutes), // ★保持（表示はしない）
        color_class: sv.color_class,
        staffId: null,
        staffName: null,
      };

      setSelectedServices([...selectedServices, item]);
    }

    if (!isEditing) {
      setSelectedDate(null);
      setSelectedStaffId(null);
      setSelectedStaffName(null);
      setSelectedStartTime(null);
      setIsFreeReservation(false);
    }
  };

  const handleNext = async () => {
    if (selectedServices.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      return;
    }
    if (!isEditing) {
      navigate('/reserve/by-service/date');
      return;
    }

    if (!storeId || !selectedDate || !selectedStartTime) {
      toast.error(t('service.incomplete'));
      return;
    }

    const serviceIds = selectedServices
      .map((service) => Number(service.serviceId))
      .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0);

    setValidating(true);
    try {
      if (isFreeReservation) {
        const staffOptions = await fetchStaffOptionsForDate({
          storeId,
          date: selectedDate,
          startTime: selectedStartTime,
          serviceIds,
        });

        if (!staffOptions.free_available) {
          setValidationMessage(
            t('service.newAnyoneUnavailableDetail'),
          );
          toast.warning(t('service.anyoneUnavailable'));
          return;
        }

        setSelectedServices(
          selectedServices.map((service) => ({
            ...service,
            staffId: null,
            staffName: ANY_STAFF_LABEL,
          })),
        );
        setValidationMessage(null);
        navigate('/reserve/by-service/confirm-reservation');
        return;
      }

      if (!selectedStaffId) {
        toast.info(t('date.selectStaffFirst'));
        navigate('/reserve/by-service/staff');
        return;
      }

      const durations = await fetchStaffServiceDurations(selectedStaffId, serviceIds);
      const durationMap = new Map(
        durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
      );
      const patchedServices = selectedServices.map((service) => ({
        ...service,
        duration_minutes:
          durationMap.get(Number(service.serviceId)) ?? Number(service.duration_minutes) ?? 0,
        staffId: selectedStaffId,
        staffName: selectedStaffName,
      }));
      const durationMinutes = patchedServices.reduce(
        (sum, service) => sum + (Number(service.duration_minutes) || 0),
        0,
      );

      const slots = await postAvailableTimesByService({
        store_id: storeId,
        staff_id: selectedStaffId,
        date: selectedDate,
        duration_minutes: durationMinutes,
        service_ids: serviceIds,
      });
      const valid = slots.some((slot) => slot.time === selectedStartTime && slot.available);
      if (!valid) {
        setValidationMessage(
          t('service.newSelectionUnavailableDetail'),
        );
        toast.warning(
          t('service.newSelectionUnavailable'),
        );
        return;
      }

      setSelectedServices(patchedServices);
      setValidationMessage(null);
      navigate('/reserve/by-service/confirm-reservation');
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || t('service.validateFailed'));
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('service.chooseTitle')}
        onBack={() =>
          navigate(
            isEditing
              ? '/reserve/by-service/confirm-reservation'
              : skippedStoreSelectionByService
                ? '/reserve'
                : '/reserve/by-service/store',
          )
        }
        step={2}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('service.loading')}</p>
      ) : rows.length === 0 ? (
        isEditing ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">{t('service.cannotChange')}</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  {t('service.noAlternative')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-400 text-sm">
            {t('service.noReservable')}
          </p>
        )
      ) : (
        <ServiceCategoryAccordion
          categories={categoryGroups}
          selectedServiceIds={selectedServiceIds}
          onToggleService={toggleService}
        />
      )}

      {validationMessage ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('service.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">{validationMessage}</div>
            </div>
          </div>
        </div>
      ) : null}

      <ReserveStepFooter
        summary={t('footer.selectedCount', { count: selectedServices.length, total: money(totalAmount) })}
        primaryLabel={validating ? t('footer.checking') : isEditing ? t('footer.apply') : t('footer.next')}
        onPrimary={handleNext}
        primaryDisabled={selectedServices.length === 0 || totalMinutes <= 0 || validating}
      />
    </div>
  );
};

export default SelectServiceByService;

type ArrayElement<T extends readonly unknown[]> = T extends readonly (infer U)[] ? U : never;
